import { ActionIcon, Grid, Paper, Title } from "@mantine/core"
import { IconDownload } from "@tabler/icons"
import { saveAs } from "file-saver"
import moment from "moment"
import { Dispatch, SetStateAction, useContext, useState } from "react"
import { AutoSizer } from "react-virtualized"
import {
  Hint,
  HorizontalGridLines,
  LineMarkSeriesPoint,
  LineSeries,
  VerticalBarSeriesPoint,
  XAxis,
  XYPlot,
  YAxis,
} from "react-vis"
import AuthenticationContext from "../../../AuthenticationContext"
import Props, { SeriesType } from "./StatsCard.types"

const TITLE_HEIGHT = 64

const downloadAsCSV = (
  token: string,
  setLoading: Dispatch<SetStateAction<boolean>>,
  type: Omit<SeriesType, "alerts">
) => {
  setLoading(true)

  fetch("https://resinglass-api.igloo.ooo/admin/export/" + type, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + token,
    },
  })
    .then((response) =>
      response.blob().then((blob) => saveAs(blob, type + ".csv"))
    )
    .finally(() => setLoading(false))
}

const StatsCard = ({ mobile, title, series, type }: Props) => {
  const [token] = useContext(AuthenticationContext)

  const [downloadLoading, setDownloadLoading] = useState<boolean>(false)
  const [hintContent, setHintContent] = useState<LineMarkSeriesPoint | null>(
    null
  )

  return (
    <Grid.Col xs={12} md={6} lg={4}>
      <Paper withBorder style={{ height: "386px" }}>
        <div
          style={{
            height: TITLE_HEIGHT + "px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Title
            order={mobile ? 5 : 4}
            style={{
              padding: "0 12px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            id={"card-title-" + title}
          >
            {title}
          </Title>
          <div
            style={{
              display: type === "alerts" ? "none" : "flex",
              marginRight: "8px",
            }}
            id={"card-actions-" + title}
          >
            <ActionIcon
              variant="transparent"
              size="lg"
              style={{
                margin: "4px",
                color: "black",
              }}
              onClick={() => downloadAsCSV(token, setDownloadLoading, type)}
              loading={downloadLoading}
            >
              <IconDownload size={20} />
            </ActionIcon>
          </div>
        </div>
        <div
          style={{
            width: "100%",
            height: `calc(100% - ${TITLE_HEIGHT}px)`,
            position: "relative",
          }}
        >
          <AutoSizer>
            {({ width, height }: { width: number; height: number }) => (
              <XYPlot
                width={width}
                height={height}
                xType="time"
                margin={{ left: 40, right: 24 }}
                onMouseLeave={() => setHintContent(null)}
                yDomain={[0, Math.max(...series.map(({ y }) => y), 5) * 1.1]}
              >
                <XAxis
                  tickFormat={(date: Date) => moment(date).format("DD/MM")}
                  tickTotal={Math.min(series.length, mobile ? 3 : 5)}
                  style={{ fontSize: 12 }}
                />
                <YAxis
                  style={{ fontSize: 12 }}
                  tickSize={0}
                  tickFormat={(x) => Math.round(x).toString()}
                />
                <HorizontalGridLines />
                <LineSeries
                  data={series}
                  onNearestX={(value: VerticalBarSeriesPoint) =>
                    setHintContent(value)
                  }
                  color="var(--contrasting-color)"
                  style={{
                    strokeWidth: 3,
                  }}
                />
                {hintContent && (
                  <Hint value={hintContent}>
                    <Paper style={{ padding: "8px" }} shadow="md" withBorder>
                      <>
                        <b>{moment(hintContent.x).format("DD/MM/YYYY")}</b>
                        <br />
                        {hintContent.y}
                      </>
                    </Paper>
                  </Hint>
                )}
              </XYPlot>
            )}
          </AutoSizer>
        </div>
      </Paper>
    </Grid.Col>
  )
}

export default StatsCard
